import { useLayoutEffect, useState, RefObject } from "react";

/**
 * Hook to detect if an element's content is overflowing (truncated)
 * @param ref - Reference to the element to check for overflow
 * @param dependencies - Array of dependencies that should trigger a recheck
 * @returns boolean indicating if the element is overflowing
 */
export function useIsOverflowing<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  dependencies: unknown[] = []
): boolean {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const checkOverflow = () => {
      if (ref.current) {
        const isCurrentlyOverflowing = ref.current.scrollWidth > ref.current.clientWidth;
        setIsOverflowing(isCurrentlyOverflowing);
      }
    };

    const timeoutId = setTimeout(checkOverflow, 0);
    
    window.addEventListener('resize', checkOverflow);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkOverflow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...dependencies]);

  return isOverflowing;
}

